import cx from 'classnames';
import React, { ElementRef, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { IconButton } from '../../../design-system/components/form/button/IconButton';
import { IconArrowBottom } from '../../../design-system/components/icons/ArrowBottom';
import { Typography } from '../../../design-system/components/typography/Typography';
import { DestinationTypeRegion } from '../../../resources/destination/domain/types/destination';
import { Region } from '../../../resources/region/domain/types/region';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './WhereToGoSectionMapPanel.module.css';

export function WhereToGoSectionMapPanel<
    R extends PickDomain<
        Region,
        'id' | 'name' | 'slug',
        {
            destination: PickDomain<Region['destination'], 'id' | 'slug'>;
        }
    >,
    DR extends PickDomain<DestinationTypeRegion, 'id' | 'slug' | 'name'>,
>({
    regions,
    destinationsTypeRegion,
    selectedRegion,
    onRegionSelect,
    onDestinationRegionSelect,
}: {
    regions: Array<R>;
    destinationsTypeRegion: Array<DR>;
    selectedRegion:
        | {
              type: 'destinationRegion';
              item: DR;
          }
        | { type: 'region'; item: R }
        | null;
    onRegionSelect: (region: { type: 'region'; item: R }) => void;
    onDestinationRegionSelect: (destinationRegion: { type: 'destinationRegion'; item: DR }) => void;
}) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();
    const scrollWrapperRef = useRef<ElementRef<'div'>>(null);
    const [isScrollBottomButtonHidden, setIsScrollBottomButtonHidden] = useState(true);

    function updateScrollBottomButtonVisibility() {
        const scrollWrapper = scrollWrapperRef.current;

        if (!scrollWrapper) {
            return;
        }

        setIsScrollBottomButtonHidden(
            scrollWrapper.scrollTop + scrollWrapper.clientHeight >= scrollWrapper.scrollHeight,
        );
    }

    function handleWrapperScroll() {
        updateScrollBottomButtonVisibility();
    }

    function handleClickScrollBottom() {
        const scrollWrapper = scrollWrapperRef.current;

        if (!scrollWrapper) {
            return;
        }

        scrollWrapper.scrollTo({
            top: scrollWrapper.scrollTop + scrollWrapper.scrollHeight,
            behavior: 'smooth',
        });
    }

    useEffect(() => {
        updateScrollBottomButtonVisibility();
    }, []);

    return (
        <div className={style.mapPanel}>
            <Typography className={style.title} as="div" scale="primary-body-bold">
                {formatMessage({
                    id: 'global.mapPanel.titleRegionRecommandations',
                })}
            </Typography>

            <div
                ref={scrollWrapperRef}
                className={style.scrollWrapper}
                onScroll={handleWrapperScroll}
            >
                {destinationsTypeRegion.map((destinationTypeRegion, index) => {
                    const isSelected = selectedRegion
                        ? selectedRegion.type === 'destinationRegion' &&
                          destinationTypeRegion.id === selectedRegion.item.id
                        : false;

                    return (
                        <Typography
                            key={destinationTypeRegion.id}
                            as="a"
                            scale="primary-xs-bold"
                            href={generatePath('destination', {
                                destinationSlug: destinationTypeRegion.slug,
                            })}
                            className={cx(style.region, {
                                [style.isSelected]: isSelected,
                            })}
                            onClick={(event) => {
                                event.preventDefault();

                                onDestinationRegionSelect({
                                    type: 'destinationRegion',
                                    item: destinationTypeRegion,
                                });

                                trackEvent((ampli, defaultProperties) => {
                                    ampli.destinationClicked({
                                        ...defaultProperties,
                                        block: 'where_to_go',
                                        card_number: index + 1,
                                        destination_type: 'region',
                                        destination_id: destinationTypeRegion.id,
                                        destination_slug: destinationTypeRegion.slug,
                                    });
                                });
                            }}
                        >
                            {destinationTypeRegion.name}
                        </Typography>
                    );
                })}

                {regions.map((region, index) => {
                    const isSelected = selectedRegion
                        ? selectedRegion.type === 'region' && region.id === selectedRegion.item.id
                        : false;

                    return (
                        <Typography
                            key={region.id}
                            as="a"
                            scale="primary-xs-bold"
                            href={generatePath('region', {
                                regionSlug: region.slug,
                                destinationSlug: region.destination.slug,
                            })}
                            className={cx(style.region, {
                                [style.isSelected]: isSelected,
                            })}
                            onClick={(event) => {
                                event.preventDefault();

                                onRegionSelect({ type: 'region', item: region });

                                trackEvent((ampli, defaultProperties) => {
                                    ampli.regionClicked({
                                        ...defaultProperties,
                                        block: 'where_to_go',
                                        card_number: index + 1,
                                        click_type: 'button',
                                        destination_id: region.destination.id,
                                        destination_slug: region.destination.slug,
                                        region_id: region.id,
                                        region_slug: region.slug,
                                    });
                                });
                            }}
                        >
                            {region.name}
                        </Typography>
                    );
                })}
            </div>

            <IconButton
                Icon={IconArrowBottom}
                className={cx(style.scrollBottomButton, {
                    [style.isScrollBottomButtonHidden]: isScrollBottomButtonHidden,
                })}
                onClick={handleClickScrollBottom}
            />
        </div>
    );
}
